// src/widgets/RadiusServerHealthWidget.jsx
// Fetch data via GET /dashboard/overview?widget=radius_server_health
// (superadmin/admin only — lihat dashboard_widget_api_patch.rb)
//
// Backend mengembalikan:
//   { total, by_health: { online: N, degraded: N, offline: N, unknown: N }, list: [...] }
//
// Pie/donut chart: 1 slice per health_status (online/degraded/offline/unknown),
// total server di tengah donut, legend + persentase di samping.

import { HeartPulse } from 'lucide-react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import Empty from './Empty';
import useWidgetData from '../hooks/useWidgetData';

const HEALTH_COLORS = {
  online:   'var(--accent-green, #22c55e)',
  degraded: 'var(--accent-orange, #f59e0b)',
  offline:  'var(--accent-red, #ef4444)',
  unknown:  'var(--text-muted, #94a3b8)',
};

const HEALTH_LABEL = {
  online:   'Online',
  degraded: 'Degraded',
  offline:  'Offline',
  unknown:  'Unknown',
};

// Urutan tampil tetap (online -> unknown) supaya legend gak lompat-lompat
// posisinya tiap refresh; status di luar daftar ini (jaga-jaga kalau backend
// nambah status baru) tetap ditampilkan, ditaruh di akhir.
const ORDER = ['online', 'degraded', 'offline', 'unknown'];

export default function RadiusServerHealthWidget() {
  const { data, loading, error } = useWidgetData('radius_server_health');

  const total    = data?.total ?? 0;
  const byHealth = data?.by_health || {};
  const knownKeys = ORDER.filter(k => byHealth[k]);
  const extraKeys = Object.keys(byHealth).filter(k => !ORDER.includes(k) && byHealth[k]);
  const items = [...knownKeys, ...extraKeys].map(status => ({
    status,
    label: HEALTH_LABEL[status] || status,
    value: byHealth[status],
    color: HEALTH_COLORS[status] || 'var(--border)',
  }));

  return (
    <div style={{
      height: '100%', display: 'flex', flexDirection: 'column',
      background: 'var(--bg-card)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)', padding: '16px 18px', boxSizing: 'border-box',
      overflow: 'hidden',
    }}>
      <div style={{
        fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)',
        marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
      }}>
        <HeartPulse size={13} color="var(--accent-green)" />
        Radius Server Health
      </div>

      {loading ? (
        <div className="skeleton" style={{ flex: 1 }} />
      ) : error ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent-red)', fontSize: 12, textAlign: 'center' }}>
          {error}
        </div>
      ) : items.length === 0 ? (
        <Empty message="Belum ada radius server" />
      ) : (
        <div style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ position: 'relative', width: '55%', height: '100%', minWidth: 110 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={items} dataKey="value" nameKey="label"
                  cx="50%" cy="50%" innerRadius="62%" outerRadius="92%"
                  paddingAngle={items.length > 1 ? 2 : 0}
                  stroke="var(--bg-card)" strokeWidth={2}
                >
                  {items.map((it, i) => <Cell key={i} fill={it.color} />)}
                </Pie>
                <Tooltip
                  contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 11 }}
                  formatter={(value, name) => [`${value} server`, name]}
                />
              </PieChart>
            </ResponsiveContainer>
            {/* Total server ditumpuk di tengah lubang donut */}
            <div style={{
              position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
              textAlign: 'center', pointerEvents: 'none',
            }}>
              <div style={{ fontSize: 20, fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>
                {total}
              </div>
              <div style={{ fontSize: 9, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Server
              </div>
            </div>
          </div>

          {/* Legend */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
            {items.map(({ status, label, value, color }) => {
              const pct = total > 0 ? ((value / total) * 100).toFixed(0) : '0';
              return (
                <div key={status} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 10, height: 10, borderRadius: 2, flexShrink: 0, background: color }} />
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {label}
                  </span>
                  <span style={{ fontSize: 12, fontFamily: 'var(--font-mono)', color: 'var(--text-primary)', fontWeight: 600 }}>
                    {value}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', minWidth: 30, textAlign: 'right' }}>
                    {pct}%
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}